import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {INTERNAL_LINKS_ID, LINK_RECOMMEND_POINTS} from '@axyseo/const/analysis';

/**
 * Assessment to check whether the text has internal links and whether they are followed or no-followed.
 */
class InternalLinksAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   * @param {number} [config.parameters.recommendedMinimum] The recommended minimum number of internal links in the text.
   * @param {number} [config.scores.allInternalFollow] The score to return if all internal links are do-follow.
   * @param {number} [config.scores.someInternalFollow] The score to return if some but not all internal links are do-follow.
   * @param {number} [config.scores.noneInternalFollow] The score to return if all internal links are no-follow.
   * @param {number} [config.scores.noInternal] The score to return if there are no internal links.
   * @param {string} [config.url] The URL to the relevant KB article.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: INTERNAL_LINKS_ID,
      fixPosition: '',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#internal-links-outbound-links',
      priority: 'high',
      title: 'Internal links',
      content: {
        good: 'Internal links are used effectively.',
        improve: '',
        bad: 'No internal links found. Add links to other relevant pages on your website.'
      }
    };

    this.identifier = INTERNAL_LINKS_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the getLinkStatistics module, based on this returns an assessment result with score.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   * @returns {AssessmentResult} The result of the assessment.
   */
  getResult({paper, researcher}) {
    this.linkStatistics = researcher.getResearch('getLinkStatistics');
    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult();
    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Checks if assessment is applicable to the paper.
   *
   * @param {Paper} paper The paper to be analyzed.
   *
   * @returns {boolean} Whether the paper has text.
   */
  isApplicable(paper) {
    return true;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    let status = 'good';
    if (this.linkStatistics.internalTotal === 0) {
      status = 'bad';
    }

    const score = this.getScore(LINK_RECOMMEND_POINTS, status);

    return {
      score,
      status
    };
  }
}

export default InternalLinksAssessment;
